import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Avatar, Spinner, Badge } from '../ui/primitives';
import { Icon } from '../ui/Icon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';

/**
 * Top-bar notifications bell.
 *
 * Behavior:
 *   - Shows a dot when there's anything to show.
 *   - Click opens a dropdown with two sections:
 *       1. Pending review (in_review uploads in workspaces where I'm staff)
 *       2. Recent rejections (rejected uploads in the last 7 days, scoped same way)
 *   - Click an item → jump to that workspace's Documents tab.
 *
 * Loads on mount, then refetches on dropdown open.
 */
export default function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState([]);   // in_review docs
  const [rejected, setRejected] = useState([]); // last 7 days rejections
  const [counts, setCounts]   = useState({ pending: 0, rejected: 0 });

  const ref = useRef(null);

  // ── Close on outside click ──
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  // ── Quick count fetch on mount (just for the dot) ──
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        // Find teams where I'm a staff member
        const { data: memberships } = await supabase
          .from('team_members')
          .select('team_id, team_roles ( is_staff )')
          .eq('user_id', user.id);
        const staffTeamIds = (memberships || [])
          .filter(m => m.team_roles?.is_staff)
          .map(m => m.team_id);
        if (cancelled || staffTeamIds.length === 0) return;

        const sevenDaysAgo = new Date(Date.now() - 7 * 86400 * 1000).toISOString();
        const [pendingRes, rejectedRes] = await Promise.all([
          supabase
            .from('document_uploads')
            .select('id', { count: 'exact', head: true })
            .in('team_id', staffTeamIds)
            .eq('status', 'in_review'),
          supabase
            .from('document_uploads')
            .select('id', { count: 'exact', head: true })
            .in('team_id', staffTeamIds)
            .eq('status', 'rejected')
            .gte('reviewed_at', sevenDaysAgo),
        ]);
        if (cancelled) return;
        setCounts({
          pending:  pendingRes.count || 0,
          rejected: rejectedRes.count || 0,
        });
      } catch (err) {
        console.warn('Notification count load failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // ── Load full data when dropdown opens ──
  const loadFull = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: memberships } = await supabase
        .from('team_members')
        .select('team_id, team_roles ( is_staff )')
        .eq('user_id', user.id);
      const staffTeamIds = (memberships || [])
        .filter(m => m.team_roles?.is_staff)
        .map(m => m.team_id);

      if (staffTeamIds.length === 0) {
        setPending([]); setRejected([]); setLoading(false);
        return;
      }

      const sevenDaysAgo = new Date(Date.now() - 7 * 86400 * 1000).toISOString();
      const [pendingRes, rejectedRes] = await Promise.all([
        supabase
          .from('document_uploads')
          .select(`
            id, team_id, original_filename, display_name, created_at,
            teams ( id, name, primary_color ),
            team_members:member_id ( first_name, last_name )
          `)
          .in('team_id', staffTeamIds)
          .eq('status', 'in_review')
          .order('created_at', { ascending: false })
          .limit(8),
        supabase
          .from('document_uploads')
          .select(`
            id, team_id, original_filename, display_name, admin_notes, reviewed_at,
            teams ( id, name, primary_color ),
            team_members:member_id ( first_name, last_name )
          `)
          .in('team_id', staffTeamIds)
          .eq('status', 'rejected')
          .gte('reviewed_at', sevenDaysAgo)
          .order('reviewed_at', { ascending: false })
          .limit(8),
      ]);

      setPending(pendingRes.data || []);
      setRejected(rejectedRes.data || []);
      setCounts({
        pending:  pendingRes.data?.length || 0,
        rejected: rejectedRes.data?.length || 0,
      });
    } catch (err) {
      console.warn('Notification load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = () => {
    if (!open) loadFull();
    setOpen(o => !o);
  };

  const totalCount = counts.pending + counts.rejected;
  const hasAny = totalCount > 0;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={toggleOpen}
        style={{
          width: 36, height: 36, border: 'none',
          background: open ? 'var(--surface-2)' : 'transparent',
          borderRadius: 8, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-2)', position: 'relative',
          transition: 'background 0.12s',
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = 'var(--surface-2)'; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'transparent'; }}
        title={hasAny ? `${totalCount} notification${totalCount === 1 ? '' : 's'}` : 'No notifications'}
      >
        <Icon.bell size={17} />
        {hasAny && (
          <span style={{
            position: 'absolute', top: 4, right: 4,
            minWidth: 12, height: 12, padding: '0 3px',
            background: counts.pending > 0 ? 'var(--accent)' : 'var(--warn)',
            color: '#fff', borderRadius: 6, fontSize: 8.5, fontWeight: 700,
            lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1.5px solid var(--surface)',
            boxSizing: 'content-box',
          }}>
            {totalCount > 9 ? '9+' : totalCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          width: 380, maxWidth: 'calc(100vw - 16px)',
          maxHeight: 480, overflowY: 'auto',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-lg)',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 100,
        }}>
          {/* Header */}
          <div style={{
            padding: '12px 14px',
            borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Notifications</div>
            {loading && <Spinner size={12} color="var(--text-muted)" />}
          </div>

          {/* Body */}
          {!loading && pending.length === 0 && rejected.length === 0 && (
            <div style={{
              padding: '40px 16px', textAlign: 'center',
              color: 'var(--text-3)', fontSize: 13,
            }}>
              <div style={{
                width: 44, height: 44, borderRadius: 12, margin: '0 auto 12px',
                background: 'var(--accent-bg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon.check size={20} stroke="var(--accent-600)" />
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                You're all caught up
              </div>
              <div style={{ fontSize: 12 }}>No documents need your attention.</div>
            </div>
          )}

          {/* Pending review section */}
          {pending.length > 0 && (
            <div>
              <SectionHeader
                label={`Pending review · ${counts.pending}`}
                icon={Icon.clock}
                color="var(--warn)"
              />
              {pending.map(d => (
                <NotificationItem
                  key={d.id}
                  doc={d}
                  variant="pending"
                  onClick={() => {
                    navigate(`/workspace/${d.team_id}/documents`);
                    setOpen(false);
                  }}
                />
              ))}
            </div>
          )}

          {/* Recent rejections */}
          {rejected.length > 0 && (
            <div>
              <SectionHeader
                label={`Recent rejections · ${counts.rejected}`}
                icon={Icon.alert}
                color="var(--danger)"
              />
              {rejected.map(d => (
                <NotificationItem
                  key={d.id}
                  doc={d}
                  variant="rejected"
                  onClick={() => {
                    navigate(`/workspace/${d.team_id}/documents`);
                    setOpen(false);
                  }}
                />
              ))}
            </div>
          )}

          {/* Footer */}
          {(pending.length > 0 || rejected.length > 0) && (
            <div style={{
              padding: '10px 14px',
              borderTop: '1px solid var(--border)',
              fontSize: 11, color: 'var(--text-3)', textAlign: 'center',
            }}>
              Showing recent activity across workspaces where you're staff.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
function SectionHeader({ label, icon: I, color }) {
  return (
    <div style={{
      padding: '10px 14px 6px',
      fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)',
      textTransform: 'uppercase', letterSpacing: '0.06em',
      display: 'flex', alignItems: 'center', gap: 6,
    }}>
      <I size={11} stroke={color || 'var(--text-3)'} />
      {label}
    </div>
  );
}

function NotificationItem({ doc, variant, onClick }) {
  const teamName = doc.teams?.name || 'Workspace';
  const teamColor = doc.teams?.primary_color || '#0a0f2e';
  const memberName = [doc.team_members?.first_name, doc.team_members?.last_name]
    .filter(Boolean).join(' ') || 'Someone';
  const ts = variant === 'pending' ? doc.created_at : doc.reviewed_at;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', padding: '10px 14px',
        display: 'flex', gap: 10, alignItems: 'flex-start',
        background: 'transparent', border: 'none',
        textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
        borderTop: '1px solid var(--border)',
        transition: 'background 0.12s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{
        width: 28, height: 28, borderRadius: 7, flexShrink: 0,
        background: teamColor, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, fontWeight: 700,
      }}>
        {teamName.split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12.5, fontWeight: 600, color: 'var(--text)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {doc.original_filename || '(unnamed)'}
        </div>
        <div style={{
          fontSize: 11, color: 'var(--text-3)', marginTop: 2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {memberName} · {teamName} · {timeAgo(ts)}
        </div>
        {variant === 'rejected' && doc.admin_notes && (
          <div style={{
            fontSize: 11, color: 'var(--danger)', marginTop: 3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {doc.admin_notes}
          </div>
        )}
      </div>
    </button>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)        return 'just now';
  if (diff < 3600)      return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400)     return Math.floor(diff / 3600) + 'h ago';
  if (diff < 86400 * 7) return Math.floor(diff / 86400) + 'd ago';
  return new Date(iso).toLocaleDateString();
}
